import {
    Column,
    Container,
    Image,
    MaterialContext,
    Panel,
    Panel9Slice,
    PanelProps,
    Plane,
    Text,
} from '@wonderlandengine/react-ui/components';
import {WindowHeader} from '../components/window-header.js';
import {Localization} from '../../utils/Localization.js';
import {Align, Justify, YogaNodeProps} from '@wonderlandengine/react-ui';
import {colorSwatch} from '../utils/colorSwatch.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import React, {
    createElement,
    forwardRef,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    FlatOpaqueTextured,
    UIColorTextured,
    UIColorTexturedAlpha,
} from '../../types/materials.js';
import {insertNewlines, parseColor} from '../utils/utils.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {GetDailyRewardParams} from '../../components/GetDailyRewardParams.js';
import {CollisionComponent, Object3D, Texture} from '@wonderlandengine/api';
import {FrameUpdateContext} from '../main-ui.js';
import {MenuButton} from '../components/menu-button.js';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';
import {MenuPanels} from '../../models/UIModel.js';

function useRewardParameters(): [string, string, boolean, Texture] {
    const [amount, setAmount] = useState(GetDailyRewardParams.instance.awardNameText);
    const [description, setDescription] = useState(
        GetDailyRewardParams.instance.descriptionText
    );
    const [showBlob, setShowBlob] = useState<boolean>(
        GetDailyRewardParams.instance.showBlob
    );

    const [rewardIcon, setRewardIcon] = useState<Texture>(
        GetDailyRewardParams.instance.awardIcon || null
    );

    useEffect(() => {
        const propChanged = (prop) => {
            if (prop === 'showBlob') {
                setShowBlob(GetDailyRewardParams.instance.showBlob);
            }
            setAmount(GetDailyRewardParams.instance.awardNameText);
            setDescription(GetDailyRewardParams.instance.descriptionText);
            setRewardIcon(GetDailyRewardParams.instance.awardIcon);
        };
        GetDailyRewardParams.instance.propertyChanged.add(propChanged);
        return () => {
            GetDailyRewardParams.instance.propertyChanged.remove(propChanged);
        };
    }, []);
    return [amount, description, showBlob, rewardIcon];
}

const BlobPresent = forwardRef<Object3D, YogaNodeProps>((props, ref) => {
    const meshParent = useRef<Object3D>(null);
    const frameUpdate = useContext(FrameUpdateContext);

    useEffect(() => {
        if (meshParent.current) {
            GetDailyRewardParams.instance.reparent(
                meshParent.current.findByName('mesh')[0]
            );

            // rescaling the collider is a bit odd and doesn't work very well.
            // That's why it's only done twice and not when rescaled. The changes
            // of someone rescaling the viewport during getting a reward is not very
            // likely anyway.
            GetDailyRewardParams.instance.cursorTargetObject.getComponent(
                CollisionComponent
            ).extents = [
                frameUpdate.relativeScale[0] * 25,
                frameUpdate.relativeScale[0] * 25,
                frameUpdate.relativeScale[0] * 25,
            ];
        }
    }, [props]);

    return (
        <Container
            {...props}
            justifyContent={Justify.Center}
            alignItems={Align.Center}
            alignContent={Align.Center}
        >
            <Plane width={100} height={100} ref={meshParent}></Plane>
        </Container>
    );
});

export const GetDailyReward = (props: React.PropsWithChildren<PanelProps> & {}) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const mats = useContext(MaterialContext);
    let material = rotbMats?.windowMaterial;

    const bm = useMemo(() => material!.clone(), []);
    (bm as UIColorTextured).setColor(parseColor(colorSwatch.Window));

    const darkBackgroundMaterial = useMemo(() => {
        const m = rotbMats.gradientMaterial!.clone() as UIColorTexturedAlpha;
        m.setColor(parseColor('#000000A0'));
        m.setFlatTexture(UiTexturesManager.instance.whiteSquare);
        return m;
    }, []);

    const shadowMaterial = rotbMats?.windowShadowMaterial;
    const gradientMaterial = rotbMats?.gradientMaterial;
    (gradientMaterial as any).setColor(parseColor(colorSwatch.WindowGradient));

    const rewardBackgroundMaterial = useMemo(() => {
        const m = mats?.panelMaterialTextured.clone() as FlatOpaqueTextured;
        m.setFlatTexture(UiTexturesManager.instance.rewardBackground);
        return m;
    }, []);

    const [amount, description, showBlob, rewardIcon] = useRewardParameters();

    return (
        <Panel
            rounding={0}
            width="100%"
            height="100%"
            justifyContent={Justify.Center}
            alignItems={Align.Center}
            material={darkBackgroundMaterial}
        >
            <Panel9Slice
                width={480}
                height={550}
                borderSize={5}
                material={shadowMaterial}
                borderTextureSize={9 / 64}
            >
                <Panel9Slice
                    left={-4}
                    top={-4}
                    width={480}
                    height={550}
                    borderSize={5}
                    material={bm}
                    borderTextureSize={9 / 64}
                    padding={5}
                >
                    <Panel
                        width="100%"
                        height="100%"
                        rounding={0}
                        material={gradientMaterial}
                    >
                        <Column
                            height="100%"
                            width={400}
                            margin={40}
                            alignSelf={Align.Center}
                        >
                            <WindowHeader
                                title={Localization.instance.get('DailyReward')}
                            />
                            <Panel
                                width={400}
                                height={240}
                                rounding={0}
                                material={rewardBackgroundMaterial}
                                justifyContent={Justify.Center}
                                alignContent={Align.Center}
                            >
                                {showBlob ? (
                                    <BlobPresent></BlobPresent>
                                ) : (
                                    <Container
                                        justifyContent={Justify.Center}
                                        alignItems={Align.Center}
                                        alignContent={Align.Center}
                                    >
                                        <Image
                                            src={rewardIcon}
                                            width={120}
                                            height={120}
                                            rounding={0}
                                        ></Image>
                                    </Container>
                                )}
                            </Panel>
                            <Panel
                                width={400}
                                height={100}
                                rounding={0}
                                backgroundColor={colorSwatch.RankProgressBackground}
                                justifyContent={Justify.FlexStart}
                            >
                                <Text
                                    marginTop={20}
                                    fontSize={22}
                                    textAlign="center"
                                    text={insertNewlines(amount, 20)}
                                ></Text>
                            </Panel>
                            <Container
                                justifyContent={Justify.Center}
                                width={400}
                                height={100}
                            >
                                {!showBlob && (
                                    <Text
                                        fontSize={18}
                                        textAlign="center"
                                        material={rotbMats.regularTextMaterial}
                                        text={description}
                                    ></Text>
                                )}
                            </Container>
                        </Column>
                    </Panel>
                </Panel9Slice>
            </Panel9Slice>
            <Container height={54} margin={6}>
                {!showBlob && (
                    <MenuButton
                        width={250}
                        height={54}
                        margin={6}
                        color={colorSwatch.AcceptButton}
                        fontSize={28}
                        onClick={() => goToPreviousMenu(MenuPanels.GetDailyReward)}
                    >
                        {Localization.instance.get('PurchaseFailedDialogAccept')}
                    </MenuButton>
                )}
            </Container>
        </Panel>
    );
};
